import { useQuery } from "@apollo/client";
import {
  PackagesByFiltersDocument,
  type PackagesByFiltersQuery,
  type PackagesByFiltersQueryVariables,
  type PackagesByFiltersInput,
  type PackageStatusEnum,
} from "@/__generated__/graphql";
import { useAuthState } from "@/store/auth";
import type { PackageData } from "@/types/package";

export const usePackagesByFiltersQuery = ({
  limit,
  status,
}: {
  limit?: number;
  status?: PackageStatusEnum | "ALL";
}) => {
  const clientId = useAuthState((state) => state.data.userInfo.id);

  const filters: PackagesByFiltersInput = {
    clientId,
    limit: limit ?? 5,
    status: status && status !== "ALL" ? status : undefined,
  };

  const result = useQuery(PackagesByFiltersDocument, {
    skip: !clientId,
    notifyOnNetworkStatusChange: true,
    variables: {
      filters,
    },
  });

  const list: PackageData[] = result.data?.packagesByFilters?.packages ?? [];
  const total = result.data?.packagesByFilters?.count ?? 0;
  const canLoadMore = list.length < total;
  const loading = result.loading;

  const loadMore = () => {
    if (!canLoadMore || loading || !clientId) return;
    result.fetchMore<PackagesByFiltersQuery, PackagesByFiltersQueryVariables>({
      variables: {
        filters: {
          ...result.variables?.filters,
          offset: list.length,
        },
      },
    });
  };

  return { ...result, list, total, canLoadMore, loading, loadMore };
};
